/**
 * WebSocket Hook for API Gateway WebSocket
 * Handles connection, disconnection, and message sending/receiving
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { getTokensFromCache } from '../../auth/utils/tokenCache';

export interface WebSocketMessage {
  conversation_id?: string;
  message?: string;
  [key: string]: any;
}

export interface UseWebSocketReturn {
  isConnected: boolean;
  isConnecting: boolean;
  connectionId: string | null;
  conversationId: string | null; // Still stored but optional for sending
  sendMessage: (message: string, conversation_id?: string, additionalData?: { user_location?: { lat: string; lng: string }; user_time?: string; trip_draft?: Record<string, any>; selected_place?: Record<string, any>; active_trip?: Record<string, any> }) => Promise<void>; // conversation_id is now optional parameter, additionalData for user_location, user_time and trip_draft
  disconnect: () => void;
  error: string | null;
}

const WEBSOCKET_ENDPOINT = process.env.REACT_APP_WEBSOCKET_ENDPOINT || '';

export const useWebSocket = (onMessage?: (message: WebSocketMessage) => void, shouldConnect: boolean = true): UseWebSocketReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 3000; // 3 seconds

  // Connect to WebSocket
  const connect = useCallback(async () => {
    if (!WEBSOCKET_ENDPOINT) {
      setError('WebSocket endpoint not configured');
      return;
    }

    // Check if already connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('✅ WebSocket already connected');
      setIsConnecting(false);
      setIsConnected(true);
      return;
    }

    // Check if connecting
    if (isConnecting || wsRef.current?.readyState === WebSocket.CONNECTING) {
      console.log('⏳ WebSocket connection already in progress');
      return;
    }

    // Clean up any existing connection before creating a new one
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch (e) {
        // Ignore errors when closing
      }
      wsRef.current = null;
    }

    setIsConnecting(true);
    setError(null);

    try {
      // Get access token from cache
      const tokens = getTokensFromCache();
      if (!tokens || !tokens.accessToken) {
        throw new Error('No access token available. Please log in.');
      }

      // Remove Bearer prefix if present
      const accessToken = tokens.accessToken.startsWith('Bearer ')
        ? tokens.accessToken.substring(7)
        : tokens.accessToken;
      
      const wsUrl = `${WEBSOCKET_ENDPOINT}?token=${encodeURIComponent(accessToken)}`;
            
      console.log('🔌 Connecting to WebSocket:', wsUrl.replace(accessToken, '***'));
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      // Connection opened
      ws.onopen = () => {
        // Check if this is still the current connection (not closed/replaced)
        if (ws !== wsRef.current) {
          // Connection was replaced, close this one
          try {
            ws.close(1000, 'Connection replaced');
          } catch (e) {
            // Ignore errors
          }
          return;
        }
        
        console.log('✅ WebSocket connected');
        setIsConnecting(false);
        reconnectAttemptsRef.current = 0;
        setIsConnected(true);
      };

      // Message received
      ws.onmessage = (event) => {
        try {
          let data: any;
          
          // Try to parse as JSON, if it fails, treat as string
          try {
            data = JSON.parse(event.data);
          } catch {
            // If not JSON, might be plain text or connection info
            data = { raw: event.data };
          }
          
          console.log('📨 WebSocket message received:', data);
          
          if (data.message === 'Connection accepted') {
            console.log('✅ Connection accepted by server');
            
            // Extract connection_id from response (format: "connection_id" with underscore)
            // This is API Gateway's connection ID (e.g., "UfADgfwQPHcCI0Q=" or "Ue_YtezWPHcCHmw=")
            const connId = data.connection_id || data.connectionId || null;
            
            if (connId) {
              setConnectionId(connId);
              (ws as any).__connectionId = connId;
              console.log('🔗 Connection ID extracted from $connect response:', connId);
            } else {
              console.warn('⚠️ Connection accepted but no connection_id in response');
            }
          }
          
          // Extract conversation_id from response (from message responses)
          // Check multiple possible formats
          let convId: string | null = null;
          
          // Try various formats for conversation_id
          if (data.conversation_id) {
            convId = data.conversation_id;
          } else if (data.conversationId) {
            convId = data.conversationId;
          } else if (data.body) {
            // Body might be a string or object
            if (typeof data.body === 'string') {
              try {
                const bodyObj = JSON.parse(data.body);
                convId = bodyObj.conversation_id || bodyObj.conversationId || null;
              } catch (e) {
                // Body is not JSON, ignore
              }
            } else if (typeof data.body === 'object') {
              convId = data.body.conversation_id || data.body.conversationId || null;
            }
          } else if (data.response) {
            // Check response object
            convId = data.response.conversation_id || data.response.conversationId || null;
          } else if (data.data) {
            // Check data object
            convId = data.data.conversation_id || data.data.conversationId || null;
          }
          
          // Update conversationId state if found
          if (convId && !conversationId) {
            setConversationId(convId);
            console.log('💬 Conversation ID received and set:', convId);
          } else if (convId && conversationId && convId !== conversationId) {
            // Conversation ID changed (shouldn't happen, but handle it)
            console.warn('⚠️ Conversation ID changed:', conversationId, '->', convId);
            setConversationId(convId);
          }
          
          // Extract message from response if present
          const responseMessage = data.message || 
                                 data.body?.message || 
                                 data.response?.message ||
                                 data.data?.message ||
                                 (typeof data.body === 'string' ? (() => {
                                   try {
                                     const bodyObj = JSON.parse(data.body);
                                     return bodyObj.message || null;
                                   } catch {
                                     return null;
                                   }
                                 })() : null);
          
          // Log all received messages for debugging (especially those without conversation_id)
          if (!convId) {
            console.log('📦 Message received (no conversation_id found):', JSON.stringify(data, null, 2));
            console.log('🔍 Looking for conversation_id in:', {
              'data.conversation_id': data.conversation_id,
              'data.conversationId': data.conversationId,
              'data.body': data.body,
              'data.response': data.response,
              'data.data': data.data
            });
          }
          
          // Always pass messages to callback, including conversation_id updates
          // Pass the full data structure so components can access all fields (text, status, etc.)
          if (onMessage) {
            // Create message object with all fields from the response
            // This includes: text, status, message_id, conversation_id, etc.
            const messageToSend: WebSocketMessage = {
              conversation_id: convId || conversationId || undefined,
              ...data, // Include ALL fields from the response (text, status, message_id, error, etc.)
              // Also include message field for backward compatibility if responseMessage exists
              ...(responseMessage && { message: responseMessage })
            };
            
            console.log('📤 Passing message to callback:', {
              conversation_id: messageToSend.conversation_id,
              status: (messageToSend as any).status,
              text: (messageToSend as any).text,
              message: messageToSend.message,
              error: (messageToSend as any).error,
              allKeys: Object.keys(messageToSend)
            });
            
            onMessage(messageToSend);
          }
        } catch (err) {
          console.error('❌ Failed to parse WebSocket message:', err);
          setError('Failed to parse message');
        }
      };

      // Connection error
      ws.onerror = (err) => {
        // Check if this is still the current connection
        if (ws !== wsRef.current) {
          // Connection was replaced, ignore this error
          return;
        }
        
        console.error('❌ WebSocket error:', err);
        console.error('❌ WebSocket error details:', {
          readyState: ws.readyState,
          url: ws.url,
          extensions: ws.extensions,
          protocol: ws.protocol
        });
        
        // Don't set error if connection is already closed (might be React StrictMode cleanup)
        if (ws.readyState !== WebSocket.CLOSED && ws.readyState !== WebSocket.CLOSING) {
          setError('WebSocket connection error. Please check your endpoint and authentication.');
        }
        setIsConnecting(false);
      };

      // Connection closed
      ws.onclose = (event) => {
        console.log('🔌 WebSocket disconnected', event.code, event.reason);
        setIsConnected(false);
        setIsConnecting(false);
        // Clear connectionId when connection closes (will be regenerated on reconnect)
        setConnectionId(null);
        if (wsRef.current) {
          (wsRef.current as any).__connectionId = null;
        }
        
        // Attempt to reconnect if not a normal closure
        if (event.code !== 1000 && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current++;
          console.log(`🔄 Attempting to reconnect (${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})...`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, RECONNECT_DELAY * reconnectAttemptsRef.current);
        } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          setError('Failed to reconnect after multiple attempts');
        }
      };

    } catch (err: any) {
      console.error('❌ Failed to connect to WebSocket:', err);
      setError(err.message || 'Failed to connect to WebSocket');
      setIsConnecting(false);
    }
  }, [onMessage]);

  // Send message via WebSocket
  // conversation_id is now passed as parameter instead of being stored in state
  const sendMessage = useCallback(async (message: string, conversation_id?: string, additionalData?: { user_location?: { lat: string; lng: string }; user_time?: string; trip_draft?: Record<string, any>; selected_place?: Record<string, any>; active_trip?: Record<string, any> }) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }

    // Use provided conversation_id (it can be undefined for the first message)
    // For new conversations, do NOT send conversation_id - backend will create it and return it
    
    // Send message according to documentation format
    // Messages are simple JSON: { "message": "...", "conversation_id": "..." (optional), "additionalData": {...} }
    // API Gateway manages connectionId internally - we don't include it in messages
    const messagePayload: any = {
      message: message
    };
    
    // Only include conversation_id if it exists (for existing conversations)
    // For new conversations, omit conversation_id entirely - backend will create and return it
    if (conversation_id && conversation_id.trim()) {
      messagePayload.conversation_id = conversation_id;
    }

    // Include additionalData if provided (user_location, user_time, trip_draft).
    // Keys are snake_case to match the Lambda contract — it reads
    // additionalData.user_location / user_time / trip_draft. (The previous
    // camelCase keys were silently ignored by the backend.)
    if (additionalData && (additionalData.user_location || additionalData.user_time || additionalData.trip_draft || additionalData.selected_place || additionalData.active_trip)) {
      messagePayload.additionalData = {};

      if (additionalData.user_location) {
        messagePayload.additionalData.user_location = additionalData.user_location;
      }

      if (additionalData.user_time) {
        messagePayload.additionalData.user_time = additionalData.user_time;
      }

      if (additionalData.trip_draft) {
        messagePayload.additionalData.trip_draft = additionalData.trip_draft;
      }

      if (additionalData.selected_place) {
        messagePayload.additionalData.selected_place = additionalData.selected_place;
      }

      if (additionalData.active_trip) {
        messagePayload.additionalData.active_trip = additionalData.active_trip;
      }
    }

    console.log('📤 Sending WebSocket message:', messagePayload);
    wsRef.current.send(JSON.stringify(messagePayload));
  }, [connectionId, conversationId]);

  // Disconnect from WebSocket
  // $disconnect route is automatically triggered by API Gateway when connection closes
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      const currentState = wsRef.current.readyState;
      
      // Just close the connection - API Gateway will automatically trigger $disconnect
      if (currentState !== WebSocket.CLOSED && currentState !== WebSocket.CLOSING) {
        try {
          wsRef.current.close(1000, 'User disconnected');
          console.log('🔌 Closing WebSocket connection (API Gateway will trigger $disconnect)');
        } catch (err) {
          // Ignore errors when closing
          console.warn('⚠️ Failed to close WebSocket:', err);
        }
      }
      
      wsRef.current = null;
    }
    
    setIsConnected(false);
    setIsConnecting(false);
    setConnectionId(null);
    setConversationId(null);
  }, []);

  // Auto-connect on mount (only once) or when shouldConnect changes
  const hasConnectedRef = useRef(false);
  const isMountedRef = useRef(true);
  
  useEffect(() => {
    // If shouldConnect is false, disconnect if connected
    if (!shouldConnect) {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        disconnect();
      }
      return;
    }

    // Prevent multiple connection attempts (React StrictMode in dev)
    if (hasConnectedRef.current) {
      return;
    }
    
    // Only connect if we have a valid endpoint and should connect
    if (!WEBSOCKET_ENDPOINT || !shouldConnect) {
      return;
    }
    
    hasConnectedRef.current = true;
    isMountedRef.current = true;
    
    // Connect once
    connect();

    // Cleanup on unmount or when shouldConnect becomes false
    return () => {
      isMountedRef.current = false;
      
      // Only disconnect if component is truly unmounting (not just React StrictMode)
      // Wait a bit to see if this is just StrictMode double-invocation
      setTimeout(() => {
        // If component remounted or shouldConnect is true, don't disconnect
        if (!isMountedRef.current && (!shouldConnect || !wsRef.current)) {
          disconnect();
        }
        // Reset flag only after delay to allow for StrictMode remount
        if (!isMountedRef.current || !shouldConnect) {
          hasConnectedRef.current = false;
        }
      }, 100);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldConnect]); // Re-run when shouldConnect changes

  // Return connection state
  // Note: connectionId is managed internally by API Gateway and not exposed to client
  // according to the documentation
  return {
    isConnected,
    isConnecting,
    connectionId: connectionId, // May be null - not needed according to docs
    conversationId,
    sendMessage,
    disconnect,
    error
  };
};